import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export default function AdminCustomersPage() {
  const [items, setItems] = useState([]);
  const [userId, setUserId] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/customers', { params: { user_id: userId || undefined, q: query || undefined } });
      setItems(res.data?.customers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load customer counts');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = items.filter((item) => {
    const haystack = `${item.owner?.name || ''} ${item.owner?.email || ''} ${item.user_id || ''}`.toLowerCase();
    const matchesQuery = !query || haystack.includes(query.toLowerCase());
    const matchesUserId = !userId || String(item.user_id || '').includes(userId);
    return matchesQuery && matchesUserId;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Customer Counts</h1>
        <p className="text-slate-500 text-sm mt-0.5">View customer counts per user. Private notes remain hidden here.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="flex flex-wrap gap-3 mt-3">
            <Input className="w-64" placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
            <Input className="w-64" placeholder="Search owner" value={query} onChange={(e) => setQuery(e.target.value)} />
            <Button type="button" variant="outline" onClick={load}>Refresh</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Total Customers</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Inactive</TableHead>
                  <TableHead>Total Paid</TableHead>
                  <TableHead>Last Customer</TableHead>
                  <TableHead>Last Payment</TableHead>
                  <TableHead>Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => (
                  <TableRow key={item.user_id}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{item.owner?.name || '-'}</div>
                      <div className="text-xs text-slate-500 break-all">{item.owner?.email || item.user_id}</div>
                    </TableCell>
                    <TableCell>{item.total_customers || 0}</TableCell>
                    <TableCell>{item.active_customers || 0}</TableCell>
                    <TableCell>{item.inactive_customers || 0}</TableCell>
                    <TableCell>UGX {Number(item.total_paid || 0).toLocaleString()}</TableCell>
                    <TableCell>{fmtDate(item.last_customer_date)}</TableCell>
                    <TableCell>{fmtDate(item.last_payment_date)}</TableCell>
                    <TableCell>
                      <Badge className={item.owner?.admin_role === 'super_admin' || item.owner?.role === 'admin' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                        {item.owner?.admin_role === 'super_admin' || item.owner?.role === 'admin' ? 'Admin' : 'User'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && !filtered.length && (
            <div className="py-10 text-center text-sm text-slate-500">No customer summaries found.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
